import React, { useEffect, useState } from 'react'
import Layout from '../components/Layout/Layout'
import LocationJob from '../components/LocationJob/LocationJob'
import { graphql, navigate } from 'gatsby'
import styled from 'styled-components'
import Hero from '../components/Hero/Hero'
import Highlight from '../components/Highlight/Highlight'
import BlockPresentation from '../components/BlockPresentation/BlockPresentation'
import pxToRem from '../utils/pxToRem'
import LocationCapacities from '../components/LocationCapacities/LocationCapacities'
import Carousel, { CarouselVariantEnum } from '../components/Carousel/Carousel'
import SlideDocument from '../components/Carousel/SlideDocument/SlideDocument'
import LocationMedia from '../components/LocationMedia/LocationMedia'
import StaticLocation from '../components/StaticLocation/StaticLocation'
import Seo from '../components/Seo/Seo'
import LocationTabs from '../components/LocationTabs/LocationTabs'
import SlideMedia from '../components/Carousel/SlideMedia/SlideMedia'
import { MainContainer } from '../theme/mainContainer.styled'
import { deviceMax } from '../theme/breakpoints'
import CorporatePresentationEnum from '../enums/CorporatePresentationEnum'
import LeadershipTab from '../components/LeadershipTab/LeadershipTab'
import LeadershipMatcher from '../interfaces/LeadershipMatcher'
import { GetFirstSentenceFromRichText } from '../utils/GetFirstSentenceFromRichText'
import useMediaQuery from '../hooks/useMediaQuery'
import { APP_AERO_URL, APP_AUTO_URL } from '../config/AppAutoAero'
import GovernanceTab from '../components/GovernanceTab/GovernanceTab'

const LIGHT = true

const ComponentTypeEnum = Object.freeze({
  location: 'componentLocationTab',
  leadership: 'componentTab',
  governance: 'componentLocationGovernanceTab',
})

const StyledLine = styled.div`
  height: auto;
  width: ${pxToRem(2)};
  margin: ${pxToRem(15)} 0;
  background-color: ${({ theme }) => theme.colors.custom.lime};
`

const StyledBlockPresentationContainer = styled.div`
  display: flex;
  gap: ${pxToRem(15)};
  width: 100%;
  max-width: ${pxToRem(767)};

  .blockPresentation {
    padding: 0;
  }

  @media ${deviceMax.tablet} {
    gap: ${pxToRem(6)};
    padding: 0 ${pxToRem(10)};
  }
`

const StyledMain = styled(MainContainer)`
  display: flex;
  width: 100%;
  margin: 0 auto;
  padding: 0;
  gap: ${pxToRem(20)};
  @media ${deviceMax.tablet} {
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    gap: ${pxToRem(30)};
    padding: 0;
  }
`

const StyledLeftSide = styled.div`
  flex: 2;
  margin: 0;
  width: 100%;
  max-width: ${pxToRem(787)};
  flex-direction: column;
  display: flex;
  @media ${deviceMax.tablet} {
    align-items: center;
  }
`
const StyledRightSide = styled.div`
  flex: 1;
  margin: 0;
  width: 100%;
  align-items: center;
  flex-direction: column;
  display: flex;
`

const StyledTabs = styled.div`
  position: relative;
  align-items: center;
  display: flex;
  margin: 0 auto;
  gap: ${pxToRem(15)};
  width: fit-content;
  padding: ${pxToRem(10)} ${pxToRem(20)};
  background: ${({ theme }) => theme.colors.secondary.whitesmoke};
  border-radius: ${pxToRem(30)};
  overflow-x: auto;

  ::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  @media ${deviceMax.mobileL} {
    max-width: 100%;
    gap: 0;
  }
`

const StyledTabTitleContainer = styled.div`
  padding: ${pxToRem(5)} ${pxToRem(20)};
  border-radius: ${pxToRem(20)};
  display: flex;
  cursor: pointer;
  ${({ $active, theme }) => {
    if ($active)
      return `
    background: ${theme.colors.custom.lime};
    `
  }}
`

const StyleDocumentCarousel = styled(Carousel)`
  padding: 0;
  @media ${deviceMax.tablet} {
    padding: ${pxToRem(20)};
  }
`

const StyledTabTitle = styled.div`
  flex-wrap: nowrap;
  white-space: nowrap;
  height: ${pxToRem(23)};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  font-size: ${({ theme }) => theme.fonts.sizes.button};
  line-height: ${({ theme }) => theme.fonts.lineHeight.cta};
`

const LocationLight = ({ pageContext, data, location }) => {
  const {
    node_locale,
    locationDetails,
    markets,
    locationTitle,
    backgroundColor,
    reorderBlocks,
    imageHeaderDesktop,
    imageHeaderMobile,
    presentationBlocTextHtml,
    presentationBlocTitle,
    documentsBlocTitle,
    documentsList,
    locationGoogleMapsUrl,
    locationInfosEmail,
    locationInfosFax,
    locationInfosCountry,
    locationInfosPhone,
    locationInfosState,
    locationInfosStreet,
    locationSuccessFactor,
    locationTabs,
    highlightItem,
    images,
    slug,
    locationInfosLocation,
    seoMetaTitle,
    seoMetaDescription,
  } = data?.contentfulTemplateLocation

  const { jobsCount, activeTabContext, parentPath, path } = pageContext
  const isMobile = useMediaQuery(deviceMax.tablet)
  const [displayMobileLayout, setDisplayMobileLayout] = useState(false)

  const [slidesDocument, setSlidesDocument] = useState(
    SlideDocument(documentsList, CarouselVariantEnum.horizontal),
  )
  const [slidesMedia, setSlidesMedia] = useState(SlideMedia(images))
  const [activeTab, setActiveTab] = useState(activeTabContext)
  const [tabType, setTabType] = useState(LeadershipMatcher.Media)
  const [target, setTarget] = useState(null)
  const [metaDesc, setMetaDesc] = useState('')
  const hightLightUrl = highlightItem?.cta?.urlAppAuto
    ? APP_AUTO_URL
    : highlightItem?.cta?.urlAppAero
      ? APP_AERO_URL
      : highlightItem?.cta?.url

  useEffect(() => {
    const metadesc = GetFirstSentenceFromRichText(
      presentationBlocTextHtml?.content,
    )
    setMetaDesc(metadesc)
  }, [])

  useEffect(() => {
    setDisplayMobileLayout(isMobile)
  }, [isMobile])

  useEffect(() => {
    if (!target) return
    const elementRect = target.parentNode.getBoundingClientRect().left
    const absoluteElementLeft = elementRect + window.pageXOffset
    // 40 = padding inside scrollable Element (20 * 2)
    const middle = absoluteElementLeft - window.innerWidth / 2 + 40
    document.querySelector('.tabs-carousel').scrollLeft += middle
    if (
      activeTab &&
      activeTab.sys?.contentType.sys.id === ComponentTypeEnum.leadership
    ) {
      const list = activeTab.contents
      if (list[0].url !== '') {
        setTabType(LeadershipMatcher.Media)
      } else {
        setTabType(LeadershipMatcher.People)
      }
    }
  }, [activeTab])

  const handleActiveTab = (event, tab) => {
    setTarget(event.target)
    setActiveTab(tab)
    if (tab) {
      const tabTitleToParam = tab.tabTitle
        .toLowerCase()
        .trim()
        .replace(/[%!?"*]+/g, '')
        .replace(/[\s_',-]+/g, '-')
        .replace(/^-+|-+$/g, '')
      navigate(`${parentPath}/${tabTitleToParam}`)
    } else {
      navigate(`${parentPath}`)
    }
  }

  const presentation = (
    <StyledBlockPresentationContainer $isMobile={isMobile}>
      <StyledLine />
      <BlockPresentation
        text={presentationBlocTextHtml}
        title={presentationBlocTitle}
      />
    </StyledBlockPresentationContainer>
  )
  const renderMainTabDesktop = () => (
    <StyledMain>
      <StyledLeftSide>
        {presentation}
        <LocationMedia images={images} />
        {slidesDocument && (
          <StyleDocumentCarousel
            title={documentsBlocTitle ? documentsBlocTitle : undefined}
            variant={CarouselVariantEnum.document}
            orientation={CarouselVariantEnum.horizontal}
          >
            {slidesDocument}
          </StyleDocumentCarousel>
        )}
        {locationDetails && (
          <LocationCapacities content={locationDetails} markets={markets} />
        )}
      </StyledLeftSide>
      <StyledRightSide>
        <StaticLocation
          locationInfosEmail={locationInfosEmail}
          locationInfosFax={locationInfosFax}
          locationInfosCountry={locationInfosCountry}
          locationInfosPhone={locationInfosPhone}
          locationInfosState={locationInfosState}
          locationInfosStreet={locationInfosStreet}
          locationInfosLocation={locationInfosLocation}
          locationGoogleMapsUrl={locationGoogleMapsUrl}
        />
        {jobsCount && jobsCount > 0 ? (
          <LocationJob
            locationKey={locationSuccessFactor}
            jobsCount={jobsCount}
            light={LIGHT}
            locale={node_locale}
          />
        ) : null}
        {highlightItem && (
          <Highlight
            ctaCardLabel={highlightItem.cta?.label}
            document={
              highlightItem.document ? highlightItem.document : undefined
            }
            image={highlightItem.image}
            textHtml={highlightItem.textHtml}
            textAlignment={highlightItem.textAlignement}
            title={highlightItem.title?.title}
            url={highlightItem.cta?.url}
          />
        )}
      </StyledRightSide>
    </StyledMain>
  )

  const renderMainTabMobile = () => (
    <StyledMain>
      <StyledLeftSide>
        {locationDetails && (
          <LocationCapacities content={locationDetails} markets={markets} />
        )}
        {slidesDocument && (
          <StyleDocumentCarousel
            title={documentsBlocTitle ? documentsBlocTitle : undefined}
            variant={CarouselVariantEnum.document}
            orientation={CarouselVariantEnum.vertical}
          >
            {slidesDocument}
          </StyleDocumentCarousel>
        )}
        {slidesMedia && (
          <Carousel variant={CarouselVariantEnum.media}>{slidesMedia}</Carousel>
        )}
      </StyledLeftSide>
      <StyledRightSide>
        {!reorderBlocks && <StaticLocation
          locationInfosEmail={locationInfosEmail}
          locationInfosFax={locationInfosFax}
          locationInfosCountry={locationInfosCountry}
          locationInfosPhone={locationInfosPhone}
          locationInfosState={locationInfosState}
          locationInfosStreet={locationInfosStreet}
          locationInfosLocation={locationInfosLocation}
          locationGoogleMapsUrl={locationGoogleMapsUrl}
        />}
        {presentation}
        {jobsCount && jobsCount > 0 ? (
          <LocationJob
            locationKey={locationSuccessFactor}
            jobsCount={jobsCount}
            light={LIGHT}
            locale={node_locale}
          />
        ) : null}
        {reorderBlocks && <StaticLocation
          locationInfosEmail={locationInfosEmail}
          locationInfosFax={locationInfosFax}
          locationInfosCountry={locationInfosCountry}
          locationInfosPhone={locationInfosPhone}
          locationInfosState={locationInfosState}
          locationInfosStreet={locationInfosStreet}
          locationInfosLocation={locationInfosLocation}
          locationGoogleMapsUrl={locationGoogleMapsUrl}
        />}
        {highlightItem && (
          <Highlight
            ctaCardLabel={highlightItem.cta?.label}
            document={
              highlightItem.document ? highlightItem.document : undefined
            }
            image={highlightItem.image}
            textHtml={highlightItem.textHtml}
            textAlignment={highlightItem.textAlignement}
            title={highlightItem.title?.title}
            url={hightLightUrl}
          />
        )}
      </StyledRightSide>
    </StyledMain>
  )

  const formattedLocationTabs = locationTabs
    ? locationTabs.map((el, index) => (
      <StyledTabTitleContainer
        key={index}
        $active={el?.tabTitle === activeTab?.tabTitle || el === activeTab}
        onClick={(e) => handleActiveTab(e, el)}
      >
        <StyledTabTitle>{el.tabTitle}</StyledTabTitle>
      </StyledTabTitleContainer>
    ))
    : null

  const currentTabInfos = activeTab ? (
    activeTab.sys?.contentType.sys.id === ComponentTypeEnum.location ? (
      <LocationTabs activeTab={activeTab} locale={node_locale} />
    ) : activeTab.sys?.contentType.sys.id === ComponentTypeEnum.governance ? (
      <GovernanceTab activeTab={activeTab} />
    ) : (
      <LeadershipTab
        activeTab={activeTab}
        type={tabType}
        location={location}
        handleActiveTab={handleActiveTab}
      ></LeadershipTab>
    )
  ) : displayMobileLayout ? (
    renderMainTabMobile()
  ) : (
    renderMainTabDesktop()
  )

  return (
    <Layout light={LIGHT}>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : locationTitle}
        description={
          seoMetaDescription ? seoMetaDescription.seoMetaDescription : metaDesc
        }
        canonicalHref={location.pathname}
      />
      <Hero
        title={locationTitle}
        backgroundColor={backgroundColor}
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        pathname={parentPath}
        isBreadcrumbsHidden={LIGHT}
      />
      {formattedLocationTabs && (
        <StyledTabs className='tabs-carousel'>
          <StyledTabTitleContainer
            $active={!activeTab}
            onClick={(e) => handleActiveTab(e, null)}
          >
            <StyledTabTitle>
              {CorporatePresentationEnum[node_locale]}
            </StyledTabTitle>
          </StyledTabTitleContainer>
          {formattedLocationTabs}
        </StyledTabs>
      )}
      {currentTabInfos}
    </Layout>
  )
}

export default LocationLight

export const query = graphql`
  query LocationLocaleQuery($contentful_id: String!, $locale: String!) {
    contentfulTemplateLocation(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      node_locale
      id
      slug
      seoMetaTitle {
        seoMetaTitle
      }
      seoMetaDescription {
        seoMetaDescription
      }
      locationTitle
      backgroundColor
      reorderBlocks
      imageHeaderDesktop {
          ...imageHeaderFragment
      }
      imageHeaderMobile {
          ...imageHeaderFragment
      }
      documentsBlocTitle
      locationLocales
      locationDetails {
        buttons {
          id
          label
          url
        }
        id
        tabTitle
        textHtml {
          content
        }
        type
      }
      markets
      images {
          ... on ContentfulContentMedia {
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
              ...mediaFragment
              
          }
          ... on ContentfulContentIframe {
          sys {
            contentType {
              sys {
                id
              }
            }
          }
              id
              iframeUrl
          }
      }
      presentationBlocTextHtml {
        content
      }
      highlightItem {
        textAlignment
        buttonVariant
        title {
          title
        }
        image {
            ...mediaFragment
        }
        textHtml {
          content
        }
        document {
            ...documentFragment
        }
        cta {
            ...ctaFragment
        }
      }
      documentsList {
          ...documentFragment
      }
      locationGoogleMapsUrl
      locationInfosEmail
      locationInfosFax
      locationInfosCountry
      locationInfosPhone
      locationInfosState
      locationInfosStreet {
        locationInfosStreet
      }
      locationSuccessFactor
      locationType {
        id
      }
      locationInfosLocation {
        lat
        lon
      }
        locationTabs {
          ... on ContentfulComponentLocationTab {
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
            tabTitle
            blocTextHtml {
              content
            }
            presentationBlocTitle
            presentationBlocTextHtml {
              content
            }
            presentationBlocDocuments {
                ...documentFragment
            }
            media {
                ...mediaFragment
            }
            largeRectangleBlocs {
              textAlignment
              expirationDate
              buttonVariant
              title {
                title
              }
              image {
                  ...mediaFragment
              }
              textHtml {
                content
              }
              document {
                  ...documentFragment
              }
              cta {
                  ...ctaFragment
              }
            }
          }
          ... on ContentfulComponentTab {
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
            id
            tabTitle
            textHtml {
              content
            }
            contents {
              ... on ContentfulComponentCard {
                id
                image {
                    ...mediaFragment
                }
                titleLevel1
                titleLevel2
                titleLevel3
                descriptionHtml {
                  content
                }
                url
              }
            }
          }
          ... on ContentfulComponentLocationGovernanceTab {
            id
            blocsContent {
              ... on ContentfulComponentFinancialResultsBloc {
                sys {
                  contentType {
                    sys {
                      id
                    }
                  }
                }
                id
                blocDescription {
                  blocDescription
                }
                blocTitle
                financialTabs {
                  tabTitle
                  contents {
                    ... on ContentfulComponentFinancialDocumentsBloc {
                      blocTitle
                      sections {
                        blocTitle
                        documents {
                          ...documentFragment
                        }
                      }
                      documents {
                          ...documentFragment
                      }
                    }
                  }
                }
              }
              ... on ContentfulContentBlocWithDocuments {
                sys {
                  contentType {
                    sys {
                      id
                    }
                  }
                }
                id
                title
                textBloc {
                  content
                }
                blocDocuments {
                    ...documentFragment
                }
              }
            }
            tabTitle
            sys {
              contentType {
                sys {
                  id
                }
              }
            }
          }
      }
    }
  }
`
